<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'
import type { StrapiFile } from '@/models'
import VideoDesignWheel from '@/assets/VideoDesignWheel.svg'
import PlayOverlayImage from '@/assets/VideoPlayIcon.svg'

const props = withDefaults(
  defineProps<{
    media?: StrapiFile
    autoplay?: boolean
    muted?: boolean
    loop?: boolean
    controls?: boolean
    customClass?: string
    blockId?: string
  }>(),
  {
    autoplay: false,
    muted: false,
    loop: true,
    controls: false,
  },
)

const isTeacherPerspective = computed(() => props.blockId?.includes('teacher') === true)

const isExpanded = ref(false)

// Keep an inline placeholder video in the carousel/page,
// and a separate fullscreen overlay video rendered via Teleport.
const inlineVideoEl = ref<HTMLVideoElement | null>(null)
const overlayVideoEl = ref<HTMLVideoElement | null>(null)

const isMutedInline = ref(!!props.muted)

const inlineShellClass = computed(() =>
  ['relative', 'w-full', isTeacherPerspective.value ? 'border-b-8 border-b-pink' : ''].join(' '),
)

const overlayShellClass = computed(() =>
  [
    'relative',
    'w-[60vw] max-w-[60vw]',
    isTeacherPerspective.value ? 'border-b-8 border-b-pink' : '',
  ].join(' '),
)

const inlineVideoClass = computed(() => [
  props.customClass ?? '',
  'block w-full h-auto',
  'opacity-70 grayscale contrast-125',
  isExpanded.value ? 'opacity-40' : '',
])

const overlayVideoClass = computed(() => [props.customClass ?? '', 'block w-full h-auto'])

async function expandAndPlay() {
  const startTime = inlineVideoEl.value?.currentTime ?? 0

  isExpanded.value = true

  await nextTick()
  await nextTick()

  if (overlayVideoEl.value) {
    try {
      overlayVideoEl.value.muted = false
      overlayVideoEl.value.currentTime = startTime
      await overlayVideoEl.value.play()
    } catch {}
  }
}

function collapse() {
  if (overlayVideoEl.value) {
    try {
      overlayVideoEl.value.pause()
      overlayVideoEl.value.currentTime = 0
    } catch {}
  }
  isExpanded.value = false
}

// Prevent background scrolling while fullscreen is open
watch(isExpanded, (v) => {
  document.documentElement.classList.toggle('overflow-hidden', v)
})
</script>

<template>
  <template v-if="media">
    <div v-if="media.mime.includes('video')" class="relative inline-block overflow-visible">
      <div :class="inlineShellClass">
        <video
          ref="inlineVideoEl"
          :src="media.url"
          :muted="isMutedInline"
          :loop="loop"
          :autoplay="autoplay"
          playsinline
          :controls="false"
          :class="inlineVideoClass"
        />

        <!-- Play overlay (only when not expanded) -->
        <button
          v-if="!isExpanded"
          type="button"
          class="absolute inset-0 z-30 flex items-center justify-center"
          @click.stop="expandAndPlay"
        >
          <img
            :src="PlayOverlayImage"
            alt=""
            class="w-16 h-16 sm:w-24 sm:h-24 hover:scale-110 transition-transform cursor-pointer"
          />
        </button>

        <!-- Design wheel (only when not expanded) -->
        <img
          v-if="!isExpanded && isTeacherPerspective"
          :src="VideoDesignWheel"
          alt=""
          class="absolute -bottom-12 -right-10 w-20 h-20 z-20 pointer-events-none"
        />
      </div>
    </div>

    <Teleport to="body" v-if="media.mime.includes('video') && isExpanded">
      <div
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-6"
        @click.self="collapse"
      >
        <div :class="overlayShellClass">
          <video
            ref="overlayVideoEl"
            :src="media.url"
            :muted="false"
            :loop="loop"
            playsinline
            :controls="true"
            :class="overlayVideoClass"
          />

          <!-- Close button -->
          <button
            type="button"
            class="absolute -top-3 -right-3 z-40 rounded-full bg-black/70 text-white px-3 py-2 hover:scale-110 transition-transform cursor-pointer"
            @click.stop="collapse"
          >
            ✕
          </button>
        </div>
      </div>
    </Teleport>

    <img v-else-if="media.mime.includes('image')" :src="media.url" alt="" :class="customClass" />
  </template>
</template>
